import { infiniteQueryOptions } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useUser } from '../User/useUser.hook';
import { InboxTypeEnum } from '@/Config/app/router/index.path';
import { ConversationApi } from '@/Domain/api/Conversation/Conversation.api';
import { User } from '@/Domain/api/User/dto/User.dto';
import { ConversationAssigneeEnum } from '@/Domain/enum/ConversationAssignee.enum';
import { ConversationStatusEnum } from '@/Domain/enum/ConversationStatus.enum';

interface ConversationsRequestConfig {
  limit: number;
  offset: number;
}

export function typeToFilters(type: InboxTypeEnum | null, user?: User) {
  if (type == null) return {};

  switch (type) {
    case InboxTypeEnum.OpenAll:
      return {
        resolution_status: ConversationStatusEnum.Open,
      };
    case InboxTypeEnum.OpenByAI:
      return {
        resolution_status: ConversationStatusEnum.Open,
        assignee_type: ConversationAssigneeEnum.AI_ASSISTANT,
      };
    case InboxTypeEnum.OpenUnassigned:
      return {
        resolution_status: ConversationStatusEnum.Open,
        assignee_type: ConversationAssigneeEnum.UNASSIGNED,
      };
    case InboxTypeEnum.OpenByMe:
      if (user == null) return {};
      return {
        resolution_status: ConversationStatusEnum.Open,
        assignee_type: ConversationAssigneeEnum.HUMAN_OPERATOR,
        assignee_user_id: String(user?.id),
      };
    // Both show every open conversation that has an assignee (a human or the AI). OpenByOther is a legacy alias narrowed to one teammate via filter_user_id in the filters memo below.
    case InboxTypeEnum.OpenAllAssigned:
    case InboxTypeEnum.OpenByOther:
      return {
        resolution_status: ConversationStatusEnum.Open,
        assigned: 'true',
      };
    case InboxTypeEnum.ResolvedAll:
      return {
        resolution_status: ConversationStatusEnum.Resolved, // TODO: Adapt endpoint to support both Resolved and ConfirmedResolved
      };
    case InboxTypeEnum.AssumedResolvedByAI:
      return {
        resolution_status: ConversationStatusEnum.Resolved,
        assignee_type: ConversationAssigneeEnum.AI_ASSISTANT,
      };
    case InboxTypeEnum.ResolvedByHuman:
      return {
        resolution_status: ConversationStatusEnum.Resolved,
        assignee_type: ConversationAssigneeEnum.HUMAN_OPERATOR,
      };
    default:
      return {};
  }
}

const LIMIT = 100;

export const useConversationList = (query: string, anchorLastMessageAt?: string | null) => {
  const [searchParams] = useSearchParams();
  const { user } = useUser();
  const inboxType = searchParams.get('type') as InboxTypeEnum | null;

  const filterUserId = searchParams.get('filter_user_id');

  const typeFilters = useMemo(() => typeToFilters(inboxType, user), [inboxType, user]);
  const baseFilters = useMemo(() => {
    const base: Record<string, string> = { ...typeFilters, query };
    if (filterUserId && inboxType === InboxTypeEnum.OpenByOther) {
      delete base.assigned;
      base.assignee_type = ConversationAssigneeEnum.HUMAN_OPERATOR;
      base.assignee_user_id = filterUserId;
    }
    return base;
  }, [inboxType, query, user, filterUserId]);

  const filters = useMemo(
    () =>
      // Omitted rather than set to null when unanchored: a null would hash to its own query
      // key and, under the app-wide gcTime: Infinity, never share data with the unanchored list.
      anchorLastMessageAt
        ? { ...baseFilters, last_message_at_end_date: anchorLastMessageAt }
        : baseFilters,
    [baseFilters, anchorLastMessageAt],
  );

  const conversationsQuery = infiniteQueryOptions({
    queryKey: ['conversations', filters],
    queryFn: async ({ pageParam }) =>
      await ConversationApi.list({ ...filters, ...(pageParam as ConversationsRequestConfig) }),
    getNextPageParam: (params, pages) => {
      if (!params?.count) return undefined;
      const { count } = params as { count: number };
      if (count < LIMIT) {
        return undefined;
      } else {
        return { limit: LIMIT, offset: pages.length * LIMIT };
      }
    },
    initialPageParam: { limit: LIMIT, offset: 0 },
    staleTime: 0,
    // Each page of a search is a full-text query, so no failure, refocused tab or reconnect repeats it.
    ...(query ? { retry: false, refetchOnWindowFocus: false, refetchOnReconnect: false } : {}),
  });

  return { conversationsQuery, typeFilters, baseFilters };
};
